import React, { FC, useMemo } from 'react';
import { Col, Row } from 'antd';
import { AdvancedFilters } from '../advanced_filters/AdvancedFilters';
import SelectedFilters from '../selected_filters_pills/SelectedFilters';
import SubnavBar from '../subnav_bar/SubnavBar';
import { buildToolbarFilterConfig } from './toolbarFilterConfig';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { INITIAL_IS_ACTIVE_FILTERS } from '../../utils/FilterUtils';

type LocationsOverviewMapToolbarVariant = 'inline' | 'fullscreen';

interface LocationsOverviewMapToolbarProps {
	filterConfig: any[];
	updateFilters: (filters: any) => void;
	clearAndUpdateFilters?: (filters: any, targetCollectionName?: string) => void;
	locations: any;
	targetCollectionName: string;
	rightActions: React.ReactNode;
	variant?: LocationsOverviewMapToolbarVariant;
}

const LocationsOverviewMapToolbar: FC<LocationsOverviewMapToolbarProps> = ({
	filterConfig,
	updateFilters,
	clearAndUpdateFilters,
	locations,
	targetCollectionName,
	rightActions,
	variant = 'fullscreen',
}) => {
	const activeFilters = nullSafeGetOrElse(`${targetCollectionName}.filters`, locations, {});
	const handleClearAndUpdateFilters =
		clearAndUpdateFilters ||
		((filters) => {
			updateFilters(filters);
		});

	// Use the same config transform ReduxDataTable applies, so the filter
	// selects resolve their values from `${targetCollectionName}.filters` and
	// stay interactive (raw configs read the wrong path and never reflect picks).
	const advancedFilterConfig = useMemo(
		() => buildToolbarFilterConfig(filterConfig, targetCollectionName),
		[filterConfig, targetCollectionName]
	);

	const filterControls = (
		<div className="flex items-center gap-2">
			<AdvancedFilters
				updateFilters={updateFilters}
				filterConfig={advancedFilterConfig}
				filtersTargetCollectionName={targetCollectionName}
				clearAndUpdateFilters={handleClearAndUpdateFilters}
				filtersStateSlice={locations}
				preAppliedFilters={{ ...INITIAL_IS_ACTIVE_FILTERS }}
				initialPagination={{ current: 1, pageSize: 250 }}
				showSearch={true}
				initialFilters={activeFilters}
			/>
		</div>
	);

	const selectedFiltersPills = (
		<SelectedFilters
			updateFilters={updateFilters}
			filterValues={activeFilters}
			filterConfig={advancedFilterConfig}
		/>
	);

	if (variant === 'inline') {
		return (
			<>
				<Row style={{ margin: '0.5em -8px' }} gutter={16}>
					<Col span={24}>
						<SubnavBar left={filterControls} right={rightActions} />
					</Col>
				</Row>
				<div className="mb-3 flex w-full flex-1 flex-row">{selectedFiltersPills}</div>
			</>
		);
	}

	return (
		<div className="shrink-0 border border-solid border-grey-5 bg-white">
			<div className="px-l py-s">
				<SubnavBar left={filterControls} right={rightActions} />
			</div>
			<div className="mb-3 flex w-full flex-row px-l">{selectedFiltersPills}</div>
		</div>
	);
};

export default LocationsOverviewMapToolbar;
